import { SongId, Song } from "../models/Song";
import { usePlaylist } from "../data/store/usePlaylist";
import { useSongs } from "../data/store/useSongs";
import MediaService from "./MediaService";

export type QueueStep = "advance" | "next" | "previous";

/**
 * Moving through the play queue (auto-advance at song end, Next, Previous).
 *
 * The queue holds song ids, and since S01/S06 a queued id is not always
 * playable by the current viewer: it may not be loaded into the songs store
 * yet (a playlist's later entries), it may be members-only/restricted for
 * this viewer (e.g. in someone's public playlist), or its media may not
 * load. Previously the callers did `retrieve(id) && setSong(...)`, so any of
 * these silently stopped playback on that entry. This loads the song when
 * needed and skips entries that can't be read or played, trying each queued
 * id at most once.
 */
export class QueuePlaybackService {
  /** The song for `id`, fetching it into the store if needed; undefined if unreadable. */
  static load = async (id: SongId): Promise<Song | undefined> => {
    const songs = useSongs.getState();
    const cached = songs.retrieve(id);
    if (cached) return cached;
    if (!songs.shouldFetch(id)) return undefined; // known deleted / not readable
    try {
      await (songs.fetch(id) as unknown as Promise<void>);
    } catch {
      return undefined;
    }
    return useSongs.getState().retrieve(id);
  };

  private static pick = async (step: QueueStep): Promise<SongId> => {
    const playlist = usePlaylist.getState();
    if (step === "previous") return playlist.previousSong();
    if (step === "next") return playlist.nextSong();
    return playlist.advance();
  };

  /** Try to start `id`; false when it can't be read or its media won't play. */
  private static tryPlay = async (id: SongId): Promise<boolean> => {
    const song = await QueuePlaybackService.load(id);
    if (!song || !song.media || !song.media.src) {
      console.warn("[queue] skipping unavailable song", id);
      return false;
    }
    try {
      await MediaService.please().setSong(song);
    } catch (error) {
      // e.g. getMediaUrl refused a signed URL for this viewer
      console.warn("[queue] skipping song whose media could not be resolved", id, error);
      return false;
    }
    usePlaylist.getState().setNowPlaying(id);
    try {
      await MediaService.please().play();
      return true;
    } catch (error: any) {
      const name = error && error.name;
      // Autoplay blocked or interrupted by another selection: the song is
      // fine, the user can press play — don't skip past it.
      if (name === "NotAllowedError" || name === "AbortError") return true;
      // NotSupportedError etc.: the file itself won't play (missing/broken).
      console.warn("[queue] skipping song that failed to play", id, error);
      return false;
    }
  };

  /** "Play All": start at the first playable song of `ids`. */
  static playFirst = async (ids: SongId[]): Promise<boolean> => {
    for (const id of ids) {
      if (await QueuePlaybackService.tryPlay(id)) return true;
    }
    return false;
  };

  /**
   * Play the next/previous playable song. Resolves true once one is playing
   * (or is loaded but the browser blocked autoplay), false when the queue has
   * nothing playable left.
   */
  static playStep = async (step: QueueStep): Promise<boolean> => {
    const tried = new Set<SongId>();
    const limit = usePlaylist.getState().songs.length + 5; // advance() may append more
    for (let i = 0; i < limit; i++) {
      const id = await QueuePlaybackService.pick(step);
      if (!id || tried.has(id)) return false;
      tried.add(id);

      if (await QueuePlaybackService.tryPlay(id)) return true;
    }
    return false;
  };
}
